import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { IPackChatThreadSummary, IPackChatMessage } from "./types";

/**
 * One line for where the conversation stands, and the way out of it.
 *
 * The action shares this row rather than taking a band of its own below the
 * reply field: the state and the escape hatch are the same thought, and the
 * copy used to have to point at a control that was nowhere near it.
 */
export const PackChatAiStatus = ({
  thread,
  managerView = false,
  action,
}: {
  thread: IPackChatThreadSummary & { messages?: IPackChatMessage[] };
  managerView?: boolean;
  /** A trailing control, when the reader has one. Sits on this same row. */
  action?: ReactNode;
}) => {
  let text: string | null = null;
  const pending =
    thread.response_owner === "ai" && thread.ai_response_status === "pending";
  if (thread.response_owner === "ai") {
    // The technician's copy stops at the state: the action is right beside it.
    if (thread.ai_response_status === "pending")
      text = managerView
        ? "AI is preparing an answer. Reply to take over."
        : "AI is preparing an answer.";
    if (thread.ai_response_status === "failed")
      text = managerView
        ? "AI could not answer yet. The question is saved. Reply to take over this conversation."
        : "AI could not answer yet. Your question is saved.";
  } else if (thread.needs_manager) {
    text = managerView
      ? "Needs a manager reply."
      : "Waiting for a manager. Replies will stay in this conversation.";
  } else if (
    thread.ai_response_status === "stopped" &&
    (thread.has_ai_messages ||
      thread.messages?.some((message) => message.event_type))
  ) {
    text = "AI replies have stopped. Replies stay in this conversation.";
  }
  if (!text && !action) return null;

  return (
    <Stack
      direction="row"
      align="center"
      gap="sm"
      fullWidth
      className="min-w-0"
    >
      {text && (
        <Stack
          role="status"
          direction="row"
          align="center"
          gap="xs"
          className="min-w-0"
        >
          {/* The wait is the only state with nothing arriving yet, so it gets
              the motion that says the answer is still coming. */}
          {pending && (
            <Loader2
              className="size-3.5 shrink-0 animate-spin text-tertiary-text"
              aria-hidden
            />
          )}
          <Typography variant="bodyW3" textColor="secondary">
            {text}
          </Typography>
        </Stack>
      )}
      {action}
    </Stack>
  );
};
